import { z } from "zod/v4"
import { api } from "../../core"
import type { User } from "firebase/auth"
import {
  type UserId,
  type SpaceKey,
  type LiveId,
  type StoryId,
  type LiveCommentId,
  type SpaceMessageId,
  type SelectionId,
  type SelectionParticipantId,
  type VoiceId,
  type VoiceReplyId,
  ViolationReports,
  ViolationReportReasonSchema,
  ViolationReportTargetKindSchema,
} from "popopo-msg"
import { assertNever } from "popopo-lib-common"
import { useZodForm } from "popopo-lib-front/zod-form"
import type { components } from "popopo-msg/api/schema"

type ApiViolationReportTarget = components["schemas"]["ViolationReportTarget"]

const schema = z.object({
  targetKind: ViolationReportTargetKindSchema,
  reason: ViolationReportReasonSchema,
  detail: z.string().max(ViolationReports.DETAIL_MAX_LENGTH).optional(),
  spaceKey: z.string().optional(),
  liveId: z.string().optional(),
  storyId: z.string().optional(),
  commentId: z.string().optional(),
  messageId: z.string().optional(),
  selectionId: z.string().optional(),
  participantId: z.string().optional(),
  voiceId: z.string().optional(),
  voiceReplyId: z.string().optional(),
})

type ViolationReportForms = z.infer<typeof schema>

function buildTarget(value: ViolationReportForms, userId: UserId): ApiViolationReportTarget | null {
  switch (value.targetKind) {
    case "user":
      return { kind: "user", userId }
    case "user-profile":
      return { kind: "user-profile", userId }
    case "space":
      if (!value.spaceKey) return null
      return { kind: "space", spaceKey: value.spaceKey as SpaceKey }
    case "story":
      if (!value.storyId) return null
      return { kind: "story", userId, storyId: value.storyId as StoryId }
    case "live-comment":
      if (!value.spaceKey || !value.liveId || !value.commentId) return null
      return {
        kind: "live-comment",
        spaceKey: value.spaceKey as SpaceKey,
        liveId: value.liveId as LiveId,
        commentId: value.commentId as LiveCommentId,
      }
    case "space-message":
      if (!value.spaceKey || !value.messageId) return null
      return {
        kind: "space-message",
        spaceKey: value.spaceKey as SpaceKey,
        messageId: value.messageId as SpaceMessageId,
      }
    case "selection-participant":
      if (!value.spaceKey || !value.liveId || !value.selectionId || !value.participantId) return null
      return {
        kind: "selection-participant",
        spaceKey: value.spaceKey as SpaceKey,
        liveId: value.liveId as LiveId,
        selectionId: value.selectionId as SelectionId,
        participantId: value.participantId as SelectionParticipantId,
      }
    case "live":
      if (!value.spaceKey || !value.liveId) return null
      return {
        kind: "live",
        spaceKey: value.spaceKey as SpaceKey,
        liveId: value.liveId as LiveId,
      }
    case "voice":
      if (!value.voiceId) return null
      return {
        kind: "voice",
        userId,
        voiceId: value.voiceId as VoiceId,
      }
    case "voice-reply":
      if (!value.voiceId || !value.voiceReplyId) return null
      return {
        kind: "voice-reply",
        userId,
        voiceId: value.voiceId as VoiceId,
        voiceReplyId: value.voiceReplyId as VoiceReplyId,
      }
    default:
      return assertNever(value.targetKind)
  }
}

export const ViolationReportForm = (props: { firebase: User, userId: UserId }) => {
  const form = useZodForm({
    schema,
    mode: "onBlur",
    defaultValues: {
      targetKind: "user",
      reason: "other",
    },
  })

  const targetKind = form.watch("targetKind")

  const onSubmit = async (value: ViolationReportForms) => {
    try {
      const idToken = await props.firebase.getIdToken()
      const target = buildTarget(value, props.userId)
      if (!target) {
        form.setError("root", { message: "通報対象の情報が不足しています。必須フィールドを入力してください。" })
        return
      }
      const response = await api.POST("/api/v2/violation-reports", {
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          target,
          reason: value.reason,
          detail: value.detail,
        },
      })
      if (response.error) {
        form.setError("root", { message: "通報の送信に失敗しました。" })
        return
      }
      form.reset()
    } catch (e) {
      console.error(e)
      form.setError("root", { message: "通報の送信中にエラーが発生しました。" })
    }
  }

  const needsSpaceKey = ["space", "live-comment", "space-message", "selection-participant", "live"].includes(targetKind)
  const needsLiveId = ["live-comment", "selection-participant", "live"].includes(targetKind)
  const needsStoryId = targetKind === "story"
  const needsCommentId = targetKind === "live-comment"
  const needsMessageId = targetKind === "space-message"
  const needsSelectionId = targetKind === "selection-participant"
  const needsParticipantId = targetKind === "selection-participant"
  const needsVoiceId = ["voice", "voice-reply"].includes(targetKind)
  const needsVoiceReplyId = targetKind === "voice-reply"
  const { isSubmitting, isValid, errors } = form.formState

  return (
    <form className="d-flex w-100 flex-column gap-3" onSubmit={form.handleSubmit(onSubmit)}>

      {errors.root && (
        <div className="alert alert-danger" role="alert">
          {errors.root.message}
        </div>
      )}

      <div className="form-floating">
        <select className="form-control" {...form.register("targetKind")}>
          {ViolationReports.targetKinds.map((kind) => (
            <option key={kind.value} value={kind.value}>
              {kind.description}
            </option>
          ))}
        </select>
        <label>通報対象の種別</label>
      </div>

      {needsSpaceKey && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("spaceKey")}
            disabled={isSubmitting}
          />
          <label>Space Key</label>
        </div>
      )}

      {needsLiveId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("liveId")}
            disabled={isSubmitting}
          />
          <label>Live ID</label>
        </div>
      )}

      {needsStoryId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("storyId")}
            disabled={isSubmitting}
          />
          <label>Story ID</label>
        </div>
      )}

      {needsCommentId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("commentId")}
            disabled={isSubmitting}
          />
          <label>Comment ID</label>
        </div>
      )}

      {needsMessageId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("messageId")}
            disabled={isSubmitting}
          />
          <label>Message ID</label>
        </div>
      )}

      {needsSelectionId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("selectionId")}
            disabled={isSubmitting}
          />
          <label>Selection ID</label>
        </div>
      )}

      {needsParticipantId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("participantId")}
            disabled={isSubmitting}
          />
          <label>Participant ID</label>
        </div>
      )}

      {needsVoiceId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("voiceId")}
            disabled={isSubmitting}
          />
          <label>Voice ID</label>
        </div>
      )}

      {needsVoiceReplyId && (
        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("voiceReplyId")}
            disabled={isSubmitting}
          />
          <label>Voice Reply ID</label>
        </div>
      )}

      <div className="form-floating">
        <select className="form-control" {...form.register("reason")} >
          {ViolationReports.reasons.map((reason) => (
            <option key={reason.value} value={reason.value}>
              {reason.description}
            </option>
          ))}
        </select>
        <label>通報の理由</label>
      </div>

      <div className="form-floating">
        <input
          className="form-control"
          {...form.register("detail")}
          disabled={isSubmitting}
        />
        <label>詳細（任意）</label>
      </div>

      <button type="submit" className="btn btn-primary form-control" disabled={isSubmitting || !isValid}>
        通報を送信
      </button>
    </form>
  )
}
